/**
 * Native JSON snapshots of databases and tables (docs/database-snapshot.md).
 *
 * Export is a plain browser download: the server streams the file and the browser writes it to
 * disk, so nothing is collected in memory. Before it starts the server is asked whether the export
 * is allowed (`check=true`), so a refusal is shown as a message instead of a saved error page.
 * Import uploads the file as the raw request body (the browser reads it from disk while sending)
 * in two steps: a read-only preview and the import, which creates something NEW and never
 * overwrites.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api, apiUpload, apiUrl, queryKeys } from '@/shared/api';

import { recordParams } from './api';
import type { CopyReport, Database, DatabaseTable, RecordFilter, SortLevel } from './types';

export interface SnapshotPreview {
  kind: 'database' | 'table';
  version: number;
  exported_at: string | null;
  includes_records: boolean;
  name: string;
  description: string | null;
  /** What the FILE claims about its source; it is unsigned and proves nothing. */
  source_confidential: boolean;
  tables: { name: string; columns: number; records: number }[];
  columns: number;
  records: number;
  dropped_user_references: number;
  /** Where and why, never values from the file. */
  problems: { path: string; code: string }[];
  valid: boolean;
}

export type ExportTarget = { kind: 'database' | 'table'; id: string };

const exportPath = ({ kind, id }: ExportTarget) =>
  kind === 'database' ? `/v1/databases/${id}/export` : `/v1/database-tables/${id}/export`;

/** Hand a streamed URL to the browser as a download (no data passes through the page). */
function startDownload(url: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = '';
  document.body.append(link);
  link.click();
  link.remove();
}

/** `json`: the native snapshot (backup, duplication); `csv` / `xlsx`: flat files for other tools. */
export type ExportFormat = 'json' | 'csv' | 'xlsx';

export const FLAT_FORMATS = ['csv', 'xlsx'] as const;

/**
 * What the open table is showing, as the export could use it: the search, filters and sort levels
 * of the working view and its visible columns in the view's order. Reported by the table so the
 * export dialog can offer "the current view" - the server then applies the same conditions itself.
 */
export interface ExportScope {
  tableId: string;
  q: string;
  filters: RecordFilter[];
  sort: SortLevel[];
  /** Visible columns in the view's order, and every column in the table's own order. */
  visibleColumns: string[];
  allColumns: string[];
}

/** Does the working view narrow or reorder the records (so "the current view" differs from all)? */
export const scopeNarrows = (scope: ExportScope): boolean =>
  scope.q.trim() !== '' || scope.filters.length > 0 || scope.sort.length > 0;

/** Do the visible columns differ from the table's (hidden ones, or another order)? */
export const scopeNarrowsColumns = (scope: ExportScope): boolean =>
  scope.visibleColumns.length !== scope.allColumns.length ||
  scope.visibleColumns.some((id, index) => id !== scope.allColumns[index]);

export interface ExportRequest {
  target: ExportTarget;
  format: ExportFormat;
  /** JSON only: with or without the records. Flat files always hold the records. */
  records: boolean;
  /** Flat table files only: use the current view's search / filters / sort. */
  scope?: ExportScope | undefined;
  /** Flat table files only: only the visible columns (in the view's order). */
  visibleColumnsOnly?: boolean;
}

/** The export URL: same `/records` query parameters, so the server filters and sorts alike. */
export function exportUrlParams({
  target,
  format,
  records,
  scope,
  visibleColumnsOnly,
}: ExportRequest): { query: Record<string, string | boolean>; repeat: Record<string, string[]> } {
  if (format === 'json' || target.kind === 'database') {
    return { query: format === 'json' ? { records } : { format }, repeat: {} };
  }
  const query: Record<string, string | boolean> = { format };
  const repeat: Record<string, string[]> = {};
  if (scope) {
    const { query: params, repeat: more } = recordParams({
      q: scope.q,
      filters: scope.filters,
      sort: scope.sort,
    });
    Object.assign(query, params);
    Object.assign(repeat, more);
    if (visibleColumnsOnly) repeat.columns = scope.visibleColumns;
  }
  return { query, repeat };
}

/**
 * Ask the server first (`check=true`: authority, scope and size limits - a refusal is a message,
 * never a saved error page), then hand the streamed URL to the browser. The mutation stays pending
 * during the check, which also stops a second click from starting a second export.
 */
export function useExportFile() {
  return useMutation({
    mutationFn: async (request: ExportRequest) => {
      const { query, repeat } = exportUrlParams(request);
      await api.get<undefined>(exportPath(request.target), {
        query: { ...query, check: true },
        repeat,
      });
      startDownload(apiUrl(exportPath(request.target), query, repeat));
    },
  });
}

interface PreviewVariables {
  /** The destination workspace (database import) - chosen in the dialog, so a variable. */
  workspaceId?: string;
  file: File;
  records: boolean;
  signal?: AbortSignal;
}

export interface ImportVariables extends PreviewVariables {
  name?: string;
  isConfidential?: boolean;
  onProgress?: (loaded: number, total: number) => void;
}

export function usePreviewDatabaseImport() {
  return useMutation({
    mutationFn: ({ workspaceId = '', file, records, signal }: PreviewVariables) =>
      apiUpload<SnapshotPreview>(`/v1/workspaces/${workspaceId}/database-import/preview`, file, {
        filename: file.name,
        query: { records },
        ...(signal && { signal }),
      }),
  });
}

export function useImportDatabase() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      workspaceId = '',
      file,
      records,
      name,
      isConfidential,
      onProgress,
      signal,
    }: ImportVariables) =>
      apiUpload<{ database: Database; report: CopyReport }>(
        `/v1/workspaces/${workspaceId}/database-import`,
        file,
        {
          filename: file.name,
          query: {
            records,
            ...(name && { name }),
            ...(isConfidential !== undefined && { is_confidential: isConfidential }),
          },
          ...(onProgress && { onProgress }),
          ...(signal && { signal }),
        },
      ),
    onSuccess: ({ database }) => {
      client.setQueryData(queryKeys.databases.detail(database.id), database);
      void client.invalidateQueries({ queryKey: queryKeys.databases.mine });
      void client.invalidateQueries({
        queryKey: queryKeys.databases.list(database.workspace_id),
      });
    },
  });
}

export function usePreviewTableImport(databaseId: string) {
  return useMutation({
    mutationFn: ({ file, records, signal }: PreviewVariables) =>
      apiUpload<SnapshotPreview>(`/v1/databases/${databaseId}/table-import/preview`, file, {
        filename: file.name,
        query: { records },
        ...(signal && { signal }),
      }),
  });
}

export function useImportTable(databaseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ file, records, name, onProgress, signal }: ImportVariables) =>
      apiUpload<{ table: DatabaseTable; report: CopyReport }>(
        `/v1/databases/${databaseId}/table-import`,
        file,
        {
          filename: file.name,
          query: { records, ...(name && { name }) },
          ...(onProgress && { onProgress }),
          ...(signal && { signal }),
        },
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.databases.tables(databaseId) }),
  });
}
